import type {
  NzbCheckResult,
  NzbCheckStats,
  NzbCheckStatsCounts,
} from '@repo/shared/api/website_api';

export const NZB_RECHECK_AFTER_DAYS = 30;
const RECHECK_AFTER_MS = NZB_RECHECK_AFTER_DAYS * 24 * 60 * 60 * 1000;

export type NzbCheckHealth = 'healthy' | 'unhealthy' | 'unknown';

export function getNzbCheckHealth(check: NzbCheckResult | undefined): NzbCheckHealth {
  if (
    !check ||
    check.outcome === 'failed' ||
    !Number.isSafeInteger(check.checked) ||
    check.checked <= 0 ||
    !Number.isSafeInteger(check.total) ||
    check.total < check.checked ||
    !Number.isSafeInteger(check.success) ||
    !Number.isSafeInteger(check.failure) ||
    check.success < 0 ||
    check.failure < 0 ||
    check.success + check.failure !== check.checked
  ) {
    return 'unknown';
  }
  return check.failure === 0 ? 'healthy' : 'unhealthy';
}

export function createEmptyNzbCheckStats(): NzbCheckStats {
  const counts = (): NzbCheckStatsCounts => ({total: 0, healthy: 0, unhealthy: 0, unknown: 0});
  return {upToDate: counts(), neverChecked: counts(), needsRecheck: counts(), all: counts()};
}

export function countNzbCheck(
  stats: NzbCheckStats,
  check: NzbCheckResult | undefined,
  asOf: number,
): void {
  const health = getNzbCheckHealth(check);
  const state = !check
    ? 'neverChecked'
    : health === 'unknown' || !Number.isFinite(check.ts) || asOf - check.ts >= RECHECK_AFTER_MS
      ? 'needsRecheck'
      : 'upToDate';
  for (const counts of [stats[state], stats.all]) {
    counts.total++;
    counts[health]++;
  }
}

export function addNzbCheckStats(left: NzbCheckStats, right: NzbCheckStats): NzbCheckStats {
  const result = createEmptyNzbCheckStats();
  for (const state of ['upToDate', 'neverChecked', 'needsRecheck', 'all'] as const) {
    for (const field of ['total', 'healthy', 'unhealthy', 'unknown'] as const) {
      result[state][field] = left[state][field] + right[state][field];
    }
  }
  return result;
}
